//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Survey\Pages\Company\AddCompany.jsx
import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Country, State, City } from "country-state-city";
import {
    Building2,
    Phone,
    Mail,
    Globe,
    MapPin,
    Landmark,
    Hash,
    ChevronRight,
    ChevronDown,
    X,
    Save,
    User,
    Calendar,
    Loader2
} from "lucide-react";
import { AddCompany as createCompanyApi } from "../../Services/MasterDataService"; // Update path to your api file

const RequiredLabel = ({ children }) => (
    <label className="mb-1.5 block text-sm font-medium text-gray-700">
        {children} <span className="text-red-500">*</span>
    </label>
);

const OptionalLabel = ({ children }) => (
    <label className="mb-1.5 block text-sm font-medium text-gray-700">
        {children} <span className="ml-1 text-xs font-normal text-gray-400">(Optional)</span>
    </label>
);

const GeoSelect = ({ icon: Icon, name, value, onChange, options, placeholder, disabled }) => (
    <div className="relative">
        <Icon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <select
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className="w-full appearance-none rounded-lg border border-gray-200 py-2.5 pl-10 pr-8 text-sm text-gray-900 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
        >
            <option value="">{placeholder}</option>
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
    </div>
);

const AddCompany = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [apiMessage, setApiMessage] = useState({ type: "", text: "" });

    const [formData, setFormData] = useState({
        companyName: "",
        contactPersonName: "",
        contactNumber: "",
        contactMail: "",
        websiteUrl: "",
        address: "",
        countryCode: "",
        countryName: "",
        stateCode: "",
        stateName: "",
        city: "",
        postalCode: "",
        establishmentDate: "",
    });

    const countryOptions = useMemo(() => {
        return Country.getAllCountries().map((c) => ({
            value: c.isoCode,
            label: c.name,
        }));
    }, []);

    const stateOptions = useMemo(() => {
        if (!formData.countryCode) return [];
        return State.getStatesOfCountry(formData.countryCode).map((s) => ({
            value: s.isoCode,
            label: s.name,
        }));
    }, [formData.countryCode]);

    const cityOptions = useMemo(() => {
        if (!formData.countryCode || !formData.stateCode) return [];
        return City.getCitiesOfState(formData.countryCode, formData.stateCode).map((c) => ({
            value: c.name,
            label: c.name,
        }));
    }, [formData.countryCode, formData.stateCode]);

    const handleClose = () => {
        navigate("/dashboard/companies");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCountryChange = (e) => {
        const countryCode = e.target.value;
        const countryObj = Country.getCountryByCode(countryCode);
        setFormData((prev) => ({
            ...prev,
            countryCode,
            countryName: countryObj ? countryObj.name : "",
            stateCode: "",
            stateName: "",
            city: "",
        }));
    };

    const handleStateChange = (e) => {
        const stateCode = e.target.value;
        const stateObj = State.getStateByCodeAndCountry(stateCode, formData.countryCode);
        setFormData((prev) => ({
            ...prev,
            stateCode,
            stateName: stateObj ? stateObj.name : "",
            city: "",
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setApiMessage({ type: "", text: "" });

        const payload = {
            companyName: formData.companyName,
            contactPersonName: formData.contactPersonName || null,
            contactNumber: formData.contactNumber,
            contactMail: formData.contactMail,
            websiteURL: formData.websiteUrl || null,
            companyAddress: formData.address,
            country: formData.countryName,
            state: formData.stateName,
            city: formData.city,
            postalCode: formData.postalCode,
            establishmentDate: formData.establishmentDate,
        };

        try {
            const response = await createCompanyApi(payload);
            const data = response.data;

            if (data?.isCreated) {
                setApiMessage({ type: "success", text: data.message || "Company added successfully!" });
                setTimeout(() => handleClose(), 1500);
            } else {
                setApiMessage({ type: "error", text: data?.message || "Failed to add company." });
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "An error occurred while saving the company.";
            setApiMessage({ type: "error", text: errorMsg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] overflow-hidden">
            <motion.button
                type="button"
                aria-label="Close add company drawer"
                onClick={handleClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
            />

            <motion.aside
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
                className="absolute right-0 top-0 h-full w-full max-w-5xl bg-[#f8f8fb] shadow-2xl"
            >
                <div className="flex h-full flex-col">
                    <div className="border-b border-gray-200 bg-white px-5 py-4 sm:px-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Company Information</h1>
                                <div className="mt-1 flex items-center gap-1.5 text-sm">
                                    <Link to="/dashboard/companies" className="text-[#7f35b2] hover:underline">
                                        Companies
                                    </Link>
                                    <ChevronRight size={14} className="text-gray-400" />
                                    <span className="text-gray-500">Add Company</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
                            <div className="mx-auto max-w-3xl">
                                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                                    <div>
                                        <h2 className="inline-block border-b-2 border-[#7f35b2] pb-1 text-lg font-semibold text-[#7f35b2]">
                                            Company Details
                                        </h2>
                                        <p className="mt-2 text-sm text-gray-500">Enter your company information to keep your profile up to date.</p>
                                    </div>

                                    {apiMessage.text && (
                                        <div className={`rounded-lg border p-3.5 text-sm ${apiMessage.type === "success"
                                            ? "border-[#c2a8f04d] bg-[#c2a8f026] text-[#48086f]"
                                            : "border-red-200 bg-red-50 text-red-700"
                                            }`}>
                                            {apiMessage.text}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <RequiredLabel>Company Name</RequiredLabel>
                                            <div className="relative">
                                                <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="text"
                                                    name="companyName"
                                                    required
                                                    value={formData.companyName}
                                                    onChange={handleChange}
                                                    placeholder="Enter company name"
                                                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <OptionalLabel>Contact Person Name</OptionalLabel>
                                            <div className="relative">
                                                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="text"
                                                    name="contactPersonName"
                                                    value={formData.contactPersonName}
                                                    onChange={handleChange}
                                                    placeholder="Enter contact person name"
                                                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <RequiredLabel>Contact Number</RequiredLabel>
                                            <div className="relative">
                                                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="tel"
                                                    name="contactNumber"
                                                    required
                                                    value={formData.contactNumber}
                                                    onChange={handleChange}
                                                    placeholder="Enter contact number"
                                                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <RequiredLabel>Contact Mail</RequiredLabel>
                                            <div className="relative">
                                                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="email"
                                                    name="contactMail"
                                                    required
                                                    value={formData.contactMail}
                                                    onChange={handleChange}
                                                    placeholder="Enter contact email"
                                                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <OptionalLabel>Website URL</OptionalLabel>
                                        <div className="relative">
                                            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="url"
                                                name="websiteUrl"
                                                value={formData.websiteUrl}
                                                onChange={handleChange}
                                                placeholder="Enter website URL"
                                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <RequiredLabel>Company Address</RequiredLabel>
                                        <div className="relative">
                                            <MapPin size={16} className="absolute left-3 top-3 text-gray-400" />
                                            <textarea
                                                name="address"
                                                rows={3}
                                                required
                                                value={formData.address}
                                                onChange={handleChange}
                                                placeholder="Enter company address"
                                                className="w-full resize-y rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        <div>
                                            <RequiredLabel>Country</RequiredLabel>
                                            <GeoSelect
                                                icon={Globe}
                                                name="countryCode"
                                                value={formData.countryCode}
                                                onChange={handleCountryChange}
                                                options={countryOptions}
                                                placeholder="Select country"
                                            />
                                        </div>
                                        <div>
                                            <RequiredLabel>State</RequiredLabel>
                                            <GeoSelect
                                                icon={Landmark}
                                                name="stateCode"
                                                value={formData.stateCode}
                                                onChange={handleStateChange}
                                                options={stateOptions}
                                                placeholder={formData.countryCode ? "Select state" : "Select country first"}
                                                disabled={!formData.countryCode}
                                            />
                                        </div>
                                        <div>
                                            <RequiredLabel>City</RequiredLabel>
                                            <GeoSelect
                                                icon={Building2}
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                options={cityOptions}
                                                placeholder={formData.stateCode ? "Select city" : "Select state first"}
                                                disabled={!formData.stateCode}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <RequiredLabel>Postal Code</RequiredLabel>
                                            <div className="relative">
                                                <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="text"
                                                    name="postalCode"
                                                    required
                                                    maxLength={10}
                                                    value={formData.postalCode}
                                                    onChange={handleChange}
                                                    placeholder="Enter postal code"
                                                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <RequiredLabel>Establishment Date</RequiredLabel>
                                            <div className="relative">
                                                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="date"
                                                    name="establishmentDate"
                                                    required
                                                    max={new Date().toISOString().split("T")[0]}
                                                    value={formData.establishmentDate}
                                                    onChange={handleChange}
                                                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                                        <p className="text-xs text-gray-400">
                                            Fields marked with <span className="text-red-500">*</span> are required
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={handleClose}
                                                disabled={loading}
                                                className="flex items-center gap-2 rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                <X size={16} />
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#3bb290] disabled:opacity-50"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Save size={16} />
                                                        Save Company
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.aside>
        </div>
    );
};

export default AddCompany;

